/**
 * Google Analytics 4 Service for Worker VM Frontend
 * Tracks AI interactions, content operations, and project management
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

interface EventProperties {
  [key: string]: any;
}

export class WorkerGA4Analytics {
  private static instance: WorkerGA4Analytics;
  private measurementId: string;
  private initialized: boolean = false;
  private userId: string | null = null;
  private projectId: string | null = null;
  private sessionStart: number;
  private debug: boolean = false;

  private constructor() {
    this.measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-B43PS4LT9C';
    this.debug = import.meta.env.DEV || false;
    this.sessionStart = Date.now();
  }

  static getInstance(): WorkerGA4Analytics {
    if (!WorkerGA4Analytics.instance) {
      WorkerGA4Analytics.instance = new WorkerGA4Analytics();
    }
    return WorkerGA4Analytics.instance;
  }

  /**
   * Initialize GA4 for Worker VM
   */
  async initialize(measurementId?: string, userId?: string): Promise<void> {
    if (this.initialized) return;

    if (measurementId) {
      this.measurementId = measurementId;
    }

    if (!this.measurementId) {
      console.warn('GA4 Worker: No measurement ID provided');
      return;
    }

    // Check if gtag is already loaded (from landing VM)
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer!.push(arguments);
      };

      window.gtag('js', new Date());
    }

    // Configure for worker VM
    window.gtag('config', this.measurementId, {
      send_page_view: false,
      debug_mode: this.debug,
      custom_map: {
        'dimension1': 'worker_id',
        'dimension2': 'project_id',
        'dimension3': 'ai_model'
      }
    });

    if (userId) {
      this.setUserId(userId);
    }

    this.initialized = true;
    this.trackEvent('worker_session_start', {
      worker_url: window.location.hostname
    });

    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
    });
  }

  /**
   * Set user ID from landing VM
   */
  setUserId(userId: string): void {
    this.userId = userId;
    if (window.gtag) {
      window.gtag('set', { user_id: userId });
    }
  }

  /**
   * Set current project context
   */
  setProjectContext(projectId: string, projectName?: string): void {
    this.projectId = projectId;
    this.trackEvent('project_context_set', {
      project_id: projectId,
      project_name: projectName
    });
  }

  /**
   * Track custom events with project context
   */
  trackEvent(eventName: string, properties?: EventProperties): void {
    if (!this.initialized) return;

    const enrichedProperties = {
      ...properties,
      user_id: this.userId,
      project_id: this.projectId,
      worker_id: window.location.hostname,
      timestamp: new Date().toISOString()
    };

    window.gtag?.('event', eventName, enrichedProperties);

    if (this.debug) {
      console.log('GA4 Worker: Event tracked', { eventName, properties: enrichedProperties });
    }
  }

  /**
   * Project tracking
   */
  trackProject = {
    created: (projectName: string, template?: string) => {
      this.trackEvent('project_created', {
        project_name: projectName,
        template,
        creation_method: template ? 'template' : 'blank'
      });
    },

    opened: (projectId: string, projectName: string) => {
      this.setProjectContext(projectId, projectName);
      this.trackEvent('project_opened', {
        project_name: projectName
      });
    },

    saved: (autoSave: boolean = false) => {
      this.trackEvent('project_saved', {
        auto_save: autoSave,
        save_time: new Date().toISOString()
      });
    },

    exported: (format: string, fileSize?: number) => {
      this.trackEvent('project_exported', {
        export_format: format,
        file_size_kb: fileSize ? Math.round(fileSize / 1024) : undefined
      });
    },

    deleted: (projectId: string) => {
      this.trackEvent('project_deleted', {
        deleted_project_id: projectId
      });
    },

    shared: (method: string, recipients?: number) => {
      this.trackEvent('project_shared', {
        share_method: method,
        recipient_count: recipients
      });
    }
  };

  /**
   * AI interaction tracking
   */
  trackAI = {
    promptSubmitted: (prompt: string, model: string, context?: any) => {
      this.trackEvent('ai_prompt_submitted', {
        prompt_length: prompt.length,
        model,
        has_context: !!context,
        prompt_preview: prompt.substring(0, 100)
      });
    },

    responseReceived: (model: string, responseTime: number, tokenCount?: number) => {
      this.trackEvent('ai_response_received', {
        model,
        response_time_ms: responseTime,
        token_count: tokenCount,
        response_speed: tokenCount ? tokenCount / (responseTime / 1000) : undefined
      });
    },

    errorOccurred: (model: string, errorType: string, errorMessage: string) => {
      this.trackEvent('ai_error', {
        model,
        error_type: errorType,
        error_message: errorMessage.substring(0, 200)
      });
    },

    modelChanged: (fromModel: string, toModel: string) => {
      this.trackEvent('ai_model_changed', {
        from_model: fromModel,
        to_model: toModel
      });
    },

    streamingToggled: (enabled: boolean) => {
      this.trackEvent('ai_streaming_toggled', {
        streaming_enabled: enabled
      });
    }
  };

  /**
   * Content operations tracking
   */
  trackContent = {
    created: (contentType: string, method: string, wordCount?: number) => {
      this.trackEvent('content_created', {
        content_type: contentType,
        creation_method: method,
        word_count: wordCount
      });
    },

    edited: (contentType: string, changeSize: number) => {
      this.trackEvent('content_edited', {
        content_type: contentType,
        change_size_chars: changeSize
      });
    },

    formatted: (formatType: string) => {
      this.trackEvent('content_formatted', {
        format_type: formatType
      });
    },

    copied: (contentType: string) => {
      this.trackEvent('content_copied', {
        content_type: contentType
      });
    },

    pasted: (source: string) => {
      this.trackEvent('content_pasted', {
        paste_source: source
      });
    }
  };

  /**
   * Feature usage tracking
   */
  trackFeature = {
    used: (featureName: string, metadata?: any) => {
      this.trackEvent('feature_used', {
        feature_name: featureName,
        ...metadata
      });
    },

    discovered: (featureName: string, discoveryMethod: string) => {
      this.trackEvent('feature_discovered', {
        feature_name: featureName,
        discovery_method: discoveryMethod
      });
    },

    configured: (featureName: string, settings: any) => {
      this.trackEvent('feature_configured', {
        feature_name: featureName,
        settings_changed: Object.keys(settings).length
      });
    }
  };

  /**
   * Collaboration tracking
   */
  trackCollaboration = {
    started: (sessionId: string, participantCount: number) => {
      this.trackEvent('collaboration_started', {
        session_id: sessionId,
        participant_count: participantCount
      });
    },

    joined: (sessionId: string, role: string) => {
      this.trackEvent('collaboration_joined', {
        session_id: sessionId,
        user_role: role
      });
    },

    messagesSent: (count: number) => {
      this.trackEvent('collaboration_message', {
        message_count: count
      });
    },

    ended: (sessionId: string, duration: number) => {
      this.trackEvent('collaboration_ended', {
        session_id: sessionId,
        duration_minutes: Math.round(duration / 60000)
      });
    }
  };

  /**
   * Performance tracking
   */
  trackPerformance = {
    pageLoad: (loadTime: number) => {
      this.trackEvent('page_load_time', {
        load_time_ms: loadTime,
        page_path: window.location.pathname
      });
    },

    apiCall: (endpoint: string, responseTime: number, success: boolean) => {
      this.trackEvent('api_call_performance', {
        endpoint,
        response_time_ms: responseTime,
        success
      });
    },

    renderTime: (componentName: string, renderTime: number) => {
      this.trackEvent('component_render_time', {
        component: componentName,
        render_time_ms: renderTime
      });
    }
  };

  /**
   * Error tracking
   */
  trackError(error: Error, context?: any): void {
    this.trackEvent('exception', {
      description: error.message,
      fatal: false,
      error_stack: error.stack?.substring(0, 500),
      error_context: context,
      page_path: window.location.pathname
    });
  }

  /**
   * Navigation tracking
   */
  trackNavigation(from: string, to: string): void {
    this.trackEvent('navigation', {
      from_page: from,
      to_page: to,
      navigation_type: 'spa'
    });
  }

  /**
   * Session tracking
   */
  trackSessionEnd(): void {
    const sessionDuration = Date.now() - this.sessionStart;
    this.trackEvent('worker_session_end', {
      session_duration_ms: sessionDuration,
      session_duration_minutes: Math.round(sessionDuration / 60000)
    });
  }

  /**
   * Custom dimension tracking
   */
  setCustomDimension(index: number, value: string): void {
    if (window.gtag) {
      window.gtag('set', {
        [`custom_dimension_${index}`]: value
      });
    }
  }

  /**
   * Enable/disable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debug = enabled;
    if (window.gtag) {
      window.gtag('config', this.measurementId, {
        debug_mode: enabled
      });
    }
  }
}

// Export singleton instance. Not auto-initialized: main.tsx initializes it through the
// consent gate (src/analytics/consentGate.ts), only when analytics is allowed.
export const workerGA4 = WorkerGA4Analytics.getInstance();